import {
  ArticleSortEnum,
  AttributeFilterFiltersInput,
  FiltersQuery,
  useArticlesQuery,
} from '@generated'
import { getAttributeIds } from '../helpers/get-attribute-ids'
import { useRCPOFilters } from './use-rcpo-filters'
import { removeAttributeDuplicates } from '../helpers/remove-attribute-duplicates'
import { hasSufficientProductData } from '../helpers/has-sufficient-product-data'

// Required attributes that are used to display the product card
// And the data in the top section of the product detail page
const RCPO_ATTRIBUTE_ID_YEAR = 65 // Year
const RCPO_ATTRIBUTE_ID_MATERIAL = 62 // Material case
const RCPO_ATTRIBUTE_ID_DIAMETER = 67 // Diameter case
export const RCPO_ATTRIBUTE_ID_COLLECTION = 81 // Collection
export const requiredRCPOAttributes = [
  RCPO_ATTRIBUTE_ID_COLLECTION,
  RCPO_ATTRIBUTE_ID_YEAR,
  RCPO_ATTRIBUTE_ID_MATERIAL,
  RCPO_ATTRIBUTE_ID_DIAMETER,
]

export type RCPOFilter = Omit<
  FiltersQuery['filters']['articleAttributeFilters'][0],
  'articleFilterAttributeValues'
> & {
  articleFilterAttributeValues: (FiltersQuery['filters']['articleAttributeFilters'][0]['articleFilterAttributeValues'][0] & {
    isAvailable: boolean
  })[]
}

type RCPOData = {
  page?: number
  sort?: ArticleSortEnum
  limit?: number
}

export const RCPO_PAGE_SIZE = 18

export function appendRequiredAttributeIds(
  filtersQuery: FiltersQuery,
  existingAttributeFilters: AttributeFilterFiltersInput[],
) {
  if (!filtersQuery) return null

  const addedRequiredAttributeFilters = requiredRCPOAttributes
    .map((attributeId) => {
      const existingAttributeFilter = existingAttributeFilters.find(
        ({ attributeID }) => attributeID === attributeId,
      )
      return (
        existingAttributeFilter ?? {
          attributeID: attributeId,
          attributeValueID: getAttributeIds(filtersQuery, attributeId),
        }
      )
    })
    .filter(({ attributeValueID }) => attributeValueID.length > 0)

  return removeAttributeDuplicates([...existingAttributeFilters, ...addedRequiredAttributeFilters])
}

export function useRCPOData({
  page = 1,
  sort = ArticleSortEnum.Populair,
  limit = RCPO_PAGE_SIZE,
}: RCPOData = {}) {
  const { defaultFilterData, attributeFilters } = useRCPOFilters()

  const [{ fetching: isFetchingArticles, data: articleData }] = useArticlesQuery({
    variables: {
      filters: {
        webID: 'gpcpreown',
        collections: [1],
        brands: [150],
        attributeFilters: appendRequiredAttributeIds(defaultFilterData, attributeFilters),
      },
      pagination: {
        limit,
        sort,
        skip: (page - 1) * limit,
      },
    },
  })

  const completeArticles = articleData?.articles?.items.filter((item) =>
    hasSufficientProductData(item),
  )

  return {
    fetching: isFetchingArticles,
    items: completeArticles || [],
    totalCount: articleData?.articles?.total || 0,
  }
}
